import React, { useMemo, useState } from "react";
import { ChevronDown } from "lucide-react";
import Markdown from "@/shared/components/markdown/Markdown";
import { CitationSources } from "@/shared/components/markdown/Citation";
import { citedSources } from "./chatSources";
import { useSmoothText } from "@/shared/hooks/useSmoothText";
import { ImageBlock } from "./MessageBlocks";
import { Elapsed, StepRows, clock, stepPhase, type AppRefs } from "./RunTimeline";
import Sources from "./Sources";
import TurnHeader from "./TurnHeader";
import ImageFile, { IMAGE_BOX } from "@/shared/components/ImageFile";
import { attachmentKeys, readAttachment } from "@/shared/api/attachments";
import { HANDOFF_TOOL } from "@/shared/api/threads";
import type { RunStep, TurnPart, WebSource } from "@/shared/types";
import type { LiveTurn } from "./types";

/* One agent turn of a server run, live or saved. One renderer for both, under one key, so
   when the saved turn replaces the live one at the run's end nothing remounts or moves.
   The turn's work, each model step's reasoning (a folded "Thinking" line), the words it said
   on the way and its tool calls in the order they happened, sits under one header
   (`TurnHeader`): open while the run works and saying what it is doing, folded once done.
   The answer, the words and pictures after the last of that work, stays below it, its
   citations drawn from the chat's web sources and the ones it cites listed under it. */

// `beforeHandoff`: words a model said as it handed the reply to the agent's other model, which
// fold with the work like words said before a call
type Said = { key: string; part: Extract<TurnPart, { kind: "thinking" | "text" }>; beforeHandoff?: boolean };
type Piece =
  | Said
  | { key: string; steps: RunStep[] }
  | { key: string; images: Extract<TurnPart, { kind: "image" }>[] };

/** The parts as they draw: a run of tool calls is one block of rows, a run of images one row
    of pictures, and words the output cap cut off one block with the next words, which carry
    them on — unless a call comes between: then the model went on to work instead. Keys stay
    put while a step grows: words, reasoning and images by their count, calls by their first id. */
function pieces(parts: TurnPart[]): Piece[] {
  const out: Piece[] = [];
  const seen = { thinking: 0, text: 0, image: 0 };
  let cut: Said | null = null; // the words the next step's carry on
  for (const part of parts) {
    const last = out.at(-1);
    if (part.kind === "text" && cut) {
      cut.part = { ...part, text: cut.part.text + part.text };
      cut = part.cut ? cut : null;
    } else if (part.kind === "tool") {
      cut = null;
      if (part.step.tool === HANDOFF_TOOL) {
        if (last && "part" in last && last.part.kind === "text") last.beforeHandoff = true;
      } else if (last && "steps" in last) last.steps.push(part.step);
      else out.push({ key: `tools:${part.step.id}`, steps: [part.step] });
    } else if (part.kind === "image") {
      if (last && "images" in last) last.images.push(part);
      else out.push({ key: `images:${seen.image++}`, images: [part] });
    } else {
      const said: Said = { key: `${part.kind}:${seen[part.kind]++}`, part };
      out.push(said);
      if (part.kind === "text" && part.cut) cut = said;
    }
  }
  return out;
}

/** A model step's reasoning, folded under one line: "Thinking" while it is written, then
    "Thought". Open, it reads in the muted style, still streaming if it is. */
function ThinkingLine({ text, writing }: { text: string; writing: boolean }) {
  const [open, setOpen] = useState(false);
  const shown = useSmoothText(text, writing);
  return (
    <div className="min-w-0">
      <button
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        className="press group -ml-1 flex items-center gap-2 pl-1 pr-2 py-1 rounded-[8px] hover:bg-[var(--surface-2)] text-left min-w-0"
      >
        <ChevronDown className={`w-3.5 h-3.5 shrink-0 text-[var(--text-quaternary)] transition-transform [transition-duration:260ms] [transition-timing-function:var(--easing)] ${open ? "" : "-rotate-90"}`} strokeWidth={1.75} />
        <span className={`text-[13.5px] ${writing ? "shimmer-text" : "text-[var(--text-tertiary)] group-hover:text-[var(--text-secondary)]"}`}>
          {writing ? "Thinking" : "Thought"}
        </span>
      </button>
      {open && (
        <div className="ml-[7px] mt-1 pl-4 border-l hairline-strong text-[13px] leading-[1.6] text-[var(--text-tertiary)] whitespace-pre-wrap break-words fade-in">
          {shown}
        </div>
      )}
    </div>
  );
}

/** Where an image still being made will land, as big as it will draw: the dots with the
    shimmer sweeping through them, the model and how long it has taken. */
function MakingImage({ model, since }: { model: string | null; since: number }) {
  return (
    <div role="status" className={`shimmer-block ${IMAGE_BOX} flex flex-col items-center justify-center gap-1 px-4 text-center`}>
      <span className="shimmer-text text-[13px] font-medium">Making an image</span>
      <span className="text-[11.5px] text-[var(--text-tertiary)]">{model && `${model} · `}<Elapsed since={since} /></span>
    </div>
  );
}

function Words({ text, writing }: { text: string; writing: boolean }) {
  return <Markdown text={useSmoothText(text, writing)} streaming={writing} />;
}

const NO_SOURCES: ReadonlyMap<number, WebSource> = new Map();

/** `live` is set while the run writes this turn; `durationMs` is a saved one's length. */
export default React.memo(function AgentTurn({ parts, live, apps, agentName, durationMs, sources = NO_SOURCES }: {
  parts: TurnPart[];
  live: LiveTurn | null;
  apps: AppRefs;
  agentName: string;
  durationMs?: number;
  /** What its answer may cite, by number (`chatSources`). */
  sources?: ReadonlyMap<number, WebSource>;
}) {
  const drawn = pieces(parts);
  const answerAt = answerStart(drawn);
  const work = drawn.slice(0, answerAt);
  const answer = drawn.slice(answerAt);
  // null until the person folds or opens it: open while the run works, folded once it's done
  const [opened, setOpened] = useState<boolean | null>(null);
  const open = work.length ? opened ?? !!live : null;
  const answerText = answer.map((piece) => ("part" in piece ? piece.part.text : "")).join("\n\n");
  const cited = useMemo(() => citedSources(answerText, sources), [answerText, sources]);
  // words a model said as it handed off are done: they move into the fold without typing again
  const draw = (piece: Piece, i: number) => (
    <TurnPiece key={piece.key} piece={piece} writing={!!live && i === drawn.length - 1 && !("part" in piece && piece.beforeHandoff)} apps={apps} agentName={agentName} />
  );
  return (
    <CitationSources.Provider value={sources}>
      <div className="space-y-3 min-w-0">
        {(live || work.length > 0) && (
          <TurnHeader
            label={live ? phaseOf(drawn, live, apps) : doneLabel(work, durationMs)}
            live={!!live}
            since={live ? Date.parse(live.startedAt) : undefined}
            open={open}
            onToggle={() => setOpened(!open)}
          />
        )}
        {work.length > 0 && (
          <div
            inert={!open}
            className={`grid transition-[grid-template-rows,opacity] [transition-duration:360ms] [transition-timing-function:var(--easing)] ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr] opacity-0 !mt-0"}`}
          >
            <div className="min-h-0 overflow-hidden -mx-1 px-1 space-y-3">{work.map(draw)}</div>
          </div>
        )}
        {answer.map((piece, i) => draw(piece, answerAt + i))}
        {cited.length > 0 && <Sources sources={cited} />}
      </div>
    </CitationSources.Provider>
  );
});

/** Where the answer starts: after the turn's last reasoning, call or handoff. Words before
    them were said on the way, and fold with the work. */
function answerStart(drawn: Piece[]): number {
  let at = 0;
  drawn.forEach((piece, i) => {
    if ("steps" in piece || ("part" in piece && (piece.part.kind === "thinking" || piece.beforeHandoff))) at = i + 1;
  });
  return at;
}

/** What a live turn is doing, from the last thing it drew: the header's label. */
function phaseOf(drawn: Piece[], live: LiveTurn, apps: AppRefs): string {
  const last = drawn.at(-1);
  if (!last || live.quiet) return "Thinking";
  if ("steps" in last) {
    const running = last.steps.find((step) => step.status === "running" || step.status === "waiting");
    return running ? stepPhase(running, apps) : "Thinking";
  }
  if ("images" in last) return "Making an image";
  if (last.part.kind === "thinking" || last.beforeHandoff) return "Thinking";  // the other model is next
  return "Writing response";
}

/** "Worked for 22s" after calls or words said before a handoff, "Thought for 4s" after reasoning alone. */
function doneLabel(work: Piece[], durationMs?: number): string {
  const verb = work.some((piece) => "steps" in piece || ("part" in piece && piece.beforeHandoff)) ? "Worked" : "Thought";
  return durationMs === undefined ? verb : `${verb} for ${clock(Math.max(1, Math.round(durationMs / 1000)))}`;
}

/** One piece as it draws; only the last piece of a live turn is still being written. */
function TurnPiece({ piece, writing, apps, agentName }: { piece: Piece; writing: boolean; apps: AppRefs; agentName: string }) {
  if ("steps" in piece) return <StepRows steps={piece.steps} apps={apps} />;
  if ("images" in piece) {
    return (
      <div className="flex flex-wrap gap-2 min-w-0">
        {piece.images.map((image, n) => {
          const alt = `Image ${n + 1} ${agentName} made`;
          if (image.from === "link") return <ImageBlock key={n} src={image.src} alt={alt} />;
          if (image.from === "file") return <ImageFile key={image.file} image={image} alt={alt} read={readAttachment} keyOf={attachmentKeys.bytes} />;
          return <MakingImage key={image.id} model={image.model} since={image.since} />;
        })}
      </div>
    );
  }
  return piece.part.kind === "thinking"
    ? <ThinkingLine text={piece.part.text} writing={writing} />
    : <Words text={piece.part.text} writing={writing} />;
}
